/**
 * SceneScriptApi — the `engine.scene` capability module (global service).
 *
 * Spawn/destroy of entities. Declared at the `system` trust tier (§9): arbitrary spawn is a
 * privileged capability, so this namespace is withheld from the sandbox `SafeEngineApi` —
 * it appears in the full `EngineApi` but NOT in `SafeEngineApi`, by type, not by hand
 * filtering. This is the module that proves the tier-narrowing projection works.
 */

import { scriptApi, scriptFn } from "../scriptApi";
import type { EntityHandle } from "../IEngineContext";

/** The injected spawn/destroy backing (Level-backed at runtime; DI per runtime). */
export interface SceneBacking {
  spawn(prefab?: string): EntityHandle;
  destroy(entity: EntityHandle): void;
  /** Runtime clone of a live entity (components + children, fresh GUIDs). Optional per runtime. */
  instantiate?(source: EntityHandle): EntityHandle;
}

@scriptApi("scene", { tier: "system", version: 1 })
export class SceneScriptApi {
  constructor(private readonly _backing: SceneBacking) {}

  @scriptFn() spawn(prefab?: string): EntityHandle {
    return this._backing.spawn(prefab);
  }

  @scriptFn() destroy(entity: EntityHandle): void {
    this._backing.destroy(entity);
  }

  /**
   * Clone an existing entity — the Unity-Instantiate primitive for prototype-driven spawning
   * (author a few template entities, stamp copies at runtime). Returns the new entity, or -1
   * if the runtime has no instantiate backing / the source doesn't resolve.
   */
  @scriptFn() instantiate(source: EntityHandle): EntityHandle {
    return this._backing.instantiate?.(source) ?? -1;
  }
}
